import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { HumanStatusCard, type HumanStatusLevel } from "./HumanStatusCard";

/**
 * SIMO-OWNER-RUN-CONSOLE-1.
 *
 * Ibo soll ohne API-Kenntnisse eine Aufgabe starten, sehen was laeuft, und
 * kritische Schritte einzeln freigeben koennen. Diese Konsole ist die
 * einzige Flaeche dafuer: Aufgabenfeld, Arbeitsliste, Detail (ueber die
 * bestehende `HumanStatusCard`) und Freigaben/Folgeaktionen — alles ohne
 * IDs, Hashes oder Statuscodes im sichtbaren Fliesstext. Technische
 * Nachweise bleiben ausschliesslich im eingeklappten Bereich der
 * `HumanStatusCard`.
 */

const RUN_POLL_INTERVAL_MS = 5000;

/**
 * SIMO-OWNER-CONSOLE-LIVE-UPDATES-CERTIFICATION-1 — Reconnect-Backoff.
 *
 * Vorher wurde nach jedem Verbindungsabbruch fest nach 2 Sekunden neu
 * verbunden. Bei einem laenger nicht erreichbaren Server erzeugt das einen
 * dauerhaften Request-Strom mit konstanter Rate. Stattdessen waechst die
 * Wartezeit exponentiell bis zu einer Obergrenze.
 */
const LANE_STREAM_RETRY_BASE_MS = 1000;
const LANE_STREAM_RETRY_MAX_MS = 30000;

/**
 * Wartezeit vor dem naechsten Verbindungsversuch.
 *
 * Bewusst eine reine Funktion: `attempt` und `jitterRatio` kommen von aussen,
 * damit das Verhalten ohne Timer und ohne Zufall testbar bleibt. Der Aufrufer
 * liefert `jitterRatio` aus `Math.random()`.
 *
 * - `attempt` wird auf `>= 1` geklemmt; unbrauchbare Werte (NaN, negativ)
 *   fallen auf den ersten Versuch zurueck statt eine Wartezeit von 0 oder
 *   NaN zu erzeugen.
 * - Der Jitter verteilt den Versuch auf 75–100 % der Basiswartezeit. Er
 *   verhindert, dass mehrere gleichzeitig geoeffnete Fenster nach einem
 *   Serverneustart im Gleichtakt erneut verbinden, kann die Wartezeit aber
 *   nie auf 0 druecken.
 */
export function laneStreamRetryDelayMs(attempt: number, jitterRatio = 0): number {
  const safeAttempt = Number.isFinite(attempt) && attempt >= 1 ? Math.floor(attempt) : 1;
  const safeJitter = Number.isFinite(jitterRatio) ? Math.min(Math.max(jitterRatio, 0), 1) : 0;
  const exponential = LANE_STREAM_RETRY_BASE_MS * 2 ** (safeAttempt - 1);
  const base = Math.min(exponential, LANE_STREAM_RETRY_MAX_MS);
  return Math.round(base * (0.75 + 0.25 * safeJitter));
}

/**
 * SIMO-OWNER-CONSOLE-LIVE-UPDATES-CERTIFICATION-1 — sichtbarer Verbindungszustand.
 *
 * Vorher war ein Streamabbruch fuer den Owner unsichtbar; die Konsole fiel
 * still auf Polling zurueck. Der Owner konnte nicht unterscheiden, ob gerade
 * nichts passiert oder ob die Live-Verbindung fehlt.
 */
export type OwnerLaneStreamStatus = "connecting" | "live" | "reconnecting";

const LANE_STREAM_STATUS_LABELS: Readonly<Record<OwnerLaneStreamStatus, string>> = {
  connecting: "Verbindung wird aufgebaut …",
  live: "Live verbunden",
  reconnecting: "Verbindung unterbrochen — neuer Versuch läuft. Die Liste wird weiter regelmäßig aktualisiert.",
};

/**
 * Deutscher Klartext je Verbindungszustand — nie der englische Code, nie ein
 * Statuscode, konsistent mit `ownerStateLabel`.
 */
export function laneStreamStatusLabel(status: string): string {
  return LANE_STREAM_STATUS_LABELS[status as OwnerLaneStreamStatus] ?? "Verbindungszustand unbekannt";
}

const ACTORS = ["codex", "claude"] as const;
type Actor = (typeof ACTORS)[number];

const SERVICE_LABELS: Readonly<Record<string, string>> = {
  codex: "Codex",
  claude: "Claude",
};

function serviceLabel(actor: string): string {
  return SERVICE_LABELS[actor] ?? actor;
}

function otherActor(actor: string): Actor {
  return actor === "codex" ? "claude" : "codex";
}

/**
 * Deutscher Klartext je Human-Status-Zustand — dieselbe Abbildung wie in
 * `HumanStatusCard.STATE_LABELS`. Nie der englische Code direkt sichtbar.
 * Deckt alle fuenf Zustaende aus dem Human Status Contract ab
 * (`apps/server/src/human-status-contract.ts`).
 */
const OWNER_STATE_LABELS: Readonly<Record<string, string>> = {
  running: "Läuft",
  succeeded: "Erfolgreich",
  limited: "Eingeschränkt",
  failed: "Fehlgeschlagen",
  blocked: "Blockiert",
};

export function ownerStateLabel(state: string): string {
  return OWNER_STATE_LABELS[state] ?? "Unbekannt";
}

/**
 * Schmale Laufzusammenfassung, wie sie `GET /api/provider-lane/runs`
 * liefert (siehe `apps/server/src/routes/provider-execution-lane.ts`).
 * Bewusst OHNE `goal` oder Providertext — die Konsole zeigt niemals Rohtext
 * aus dem Lauf, nur den bereits redigierten Human Status Contract in der
 * Detailansicht.
 */
export interface OwnerRunSummary {
  runId: string;
  actor: string;
  state: string;
  phase: string;
  failureClass: string | null;
  startedAt: string;
  applyEligible: boolean;
  approvedCount: number;
  availableActionIds: string[];
  /**
   * SIMO-FORGEJO-PR-LANE-1 (Teil 2). Optional statt Pflicht, weil aeltere
   * Laeufe (vor dieser Migration) keinen persistierten PR haben und der
   * Server dafuer `null` liefert.
   */
  pullRequestNumber?: number | null;
  pullRequestUrl?: string | null;
  /**
   * SIMO-FORGEJO-PR-STATE-SYNC-1. Ebenfalls optional wie die beiden Felder
   * oben — Laeufe ohne PR oder ohne bisherigen Abgleich liefern hier `null`.
   */
  pullRequestState?: string | null;
  pullRequestCheckedAt?: string | null;
}

function isOwnerRunSummary(value: unknown): value is OwnerRunSummary {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.runId === "string" &&
    typeof entry.actor === "string" &&
    typeof entry.state === "string" &&
    typeof entry.phase === "string" &&
    (entry.failureClass === null || typeof entry.failureClass === "string") &&
    typeof entry.startedAt === "string" &&
    typeof entry.applyEligible === "boolean" &&
    typeof entry.approvedCount === "number" &&
    Array.isArray(entry.availableActionIds) &&
    entry.availableActionIds.every((id) => typeof id === "string") &&
    (entry.pullRequestNumber === undefined || entry.pullRequestNumber === null || typeof entry.pullRequestNumber === "number") &&
    (entry.pullRequestUrl === undefined || entry.pullRequestUrl === null || typeof entry.pullRequestUrl === "string") &&
    (entry.pullRequestState === undefined || entry.pullRequestState === null || typeof entry.pullRequestState === "string") &&
    (entry.pullRequestCheckedAt === undefined || entry.pullRequestCheckedAt === null || typeof entry.pullRequestCheckedAt === "string")
  );
}

/**
 * SIMO-OWNER-CONSOLE-LIVE-UPDATES-1 — der lokale Zustand, den der SSE-Stream
 * (`api.providerLaneStream`) treibt: die aktuelle Laufliste plus die zuletzt
 * angewendete Sequenznummer.
 */
export interface OwnerLaneStreamState {
  runs: OwnerRunSummary[];
  lastSeq: number;
}

export interface OwnerLaneStreamEvent {
  type: "snapshot" | "run";
  seq: number;
  runs?: OwnerRunSummary[];
  run?: OwnerRunSummary;
}

/**
 * Reine Reduktionsfunktion fuer ein einzelnes Stream-Ereignis.
 *
 * `seq <= state.lastSeq` liefert den UNVERAENDERTEN Zustand zurueck — das ist
 * der Schutz vor doppelt zugestellten und vor veralteten Ereignissen (z.B.
 * nach einer Wiederaufnahme, die denselben Bereich ueberlappend nachliefert).
 * `snapshot` ersetzt die komplette Liste, `run` aktualisiert einen
 * vorhandenen Eintrag an Ort und Stelle oder fuegt einen neuen vorne ein.
 * Zwei unabhaengige Aufrufer, die dieselbe Ereignisfolge in derselben
 * Reihenfolge anwenden, landen dadurch immer beim selben Ergebnis
 * (Mehrfenster-Konsistenz) — reine Funktion, kein verstecktes Timing.
 */
export function applyLaneStreamEvent(
  state: OwnerLaneStreamState,
  event: OwnerLaneStreamEvent,
): OwnerLaneStreamState {
  if (event.seq <= state.lastSeq) return state;
  if (event.type === "snapshot") {
    return { runs: event.runs ?? [], lastSeq: event.seq };
  }
  if (event.type === "run" && event.run) {
    const run = event.run;
    const index = state.runs.findIndex((entry) => entry.runId === run.runId);
    const runs =
      index === -1
        ? [run, ...state.runs]
        : state.runs.map((entry, entryIndex) => (entryIndex === index ? run : entry));
    return { runs, lastSeq: event.seq };
  }
  return { ...state, lastSeq: event.seq };
}

/**
 * Validiert ein rohes Stream-Frame gegen dieselbe Allowlist wie
 * `isOwnerRunSummary` — der Client vertraut keinem Feld, das nicht bereits
 * fuer die Polling-Antwort geprueft wird.
 */
function sanitizeLaneStreamEvent(raw: {
  type: "snapshot" | "run";
  seq: number;
  runs?: unknown[];
  run?: unknown;
}): OwnerLaneStreamEvent | null {
  if (raw.type === "snapshot") {
    if (!Array.isArray(raw.runs)) return null;
    return { type: "snapshot", seq: raw.seq, runs: raw.runs.filter(isOwnerRunSummary) };
  }
  if (raw.type === "run") {
    if (!isOwnerRunSummary(raw.run)) return null;
    return { type: "run", seq: raw.seq, run: raw.run };
  }
  return null;
}

export interface GroupedOwnerRuns {
  running: OwnerRunSummary[];
  waiting: OwnerRunSummary[];
  failed: OwnerRunSummary[];
  done: OwnerRunSummary[];
}

/**
 * Gruppiert Laeufe in verstaendliche Owner-Zustaende. "Wartet auf dich" hat
 * bewusst Vorrang vor dem rohen Zustand: ein Lauf mit `applyEligible &&
 * approvedCount === 0` ist fuer den Owner in erster Linie ein offener
 * Punkt — auch wenn der technische Zustand bereits "erfolgreich" waere.
 * SIMO-RUN-RESTART-RECOVERY-ASSESSMENT-1: `blocked` ist serverseitig jetzt
 * erreichbar — es ist der Zustand eines Laufs, der durch einen Neustart
 * unterbrochen wurde, nachdem er bereits gepusht oder einen Pull Request
 * erstellt hatte. SIMO wiederholt ihn bewusst nicht von selbst. Damit ist er
 * kein "fehlgeschlagener" Lauf, den man einfach neu startet, sondern ein
 * offener Punkt, der eine Owner-Entscheidung braucht — er gehoert deshalb
 * unter "Wartet auf dich" und nicht unter "Fehlgeschlagen".
 */
export function groupRunsForOwner(runs: OwnerRunSummary[]): GroupedOwnerRuns {
  const grouped: GroupedOwnerRuns = { running: [], waiting: [], failed: [], done: [] };
  for (const run of runs) {
    if (run.state === "blocked" || (run.applyEligible && run.approvedCount === 0)) {
      grouped.waiting.push(run);
    } else if (run.state === "running") {
      grouped.running.push(run);
    } else if (run.state === "failed") {
      grouped.failed.push(run);
    } else {
      grouped.done.push(run);
    }
  }
  return grouped;
}

const OPERATION_LABELS: Readonly<Record<string, string>> = {
  apply_worktree: "Änderungen übernehmen",
  local_commit: "Commit erstellen",
  push_branch: "Branch übertragen",
};

/**
 * Welche Operationen der Owner ueber die Konsole freigeben darf.
 *
 * HARTE REGEL: `create_pr` erscheint hier NIEMALS — auch nicht, wenn
 * `approve_operations` unter den verfuegbaren Aktionen ist. Der
 * Pull-Request-Schritt laeuft heute gegen einen lokalen vertragskonformen
 * Test-Endpunkt, nicht gegen einen echten Forgejo-PR. Eine produktiv
 * aussehende Schaltflaeche dafuer waere eine Scheinbedienung. Freigeben
 * lassen sich ausschliesslich `apply_worktree`, `local_commit` und
 * `push_branch`.
 */
export function approvableOperations(availableActionIds: string[]): string[] {
  if (!availableActionIds.includes("approve_operations")) return [];
  return ["apply_worktree", "local_commit", "push_branch"];
}

export interface PullRequestPresentation {
  mode: "create" | "open" | "created_without_link" | "none";
  url: string | null;
  number: number | null;
}

/**
 * SIMO-FORGEJO-PR-LANE-1 (Teil 2).
 *
 * Reine Ableitung, welche PR-Bedienung die Konsole gerade zeigen darf:
 *
 *  - `create`: der Server bietet `create_pull_request` an — noch kein PR.
 *  - `open`: der Server bietet `open_pull_request` an — ein echter,
 *    dauerhaft gespeicherter PR mit einer bereits serverseitig geprueft
 *    vertrauenswuerdigen URL existiert (`assertTrustedPullRequestUrl`).
 *  - `created_without_link`: eine PR-Nummer ist bekannt, aber weder
 *    "erstellen" noch "oeffnen" ist verfuegbar (keine vertrauenswuerdige
 *    URL) — nur ein ruhiger Hinweistext, niemals ein Link.
 *  - `none`: keine PR-Bedienung fuer diesen Lauf.
 *
 * `technical` (aus dem Human Status Contract) ist bewusst NICHT die Quelle
 * fuer die PR-Daten: `technical` existiert nur in der Stufe `technical` und
 * waere damit fuer `simple`/`standard` nicht verfuegbar. Die PR-Daten kommen
 * stattdessen aus dem Lauf-Listeneintrag (`pullRequestNumber`/`pullRequestUrl`
 * aus `GET /api/provider-lane/runs`), der in jeder Stufe vorhanden ist.
 */
export function pullRequestPresentation(
  actionIds: string[],
  run: { pullRequestNumber?: number | null; pullRequestUrl?: string | null } | null,
): PullRequestPresentation {
  const number = run?.pullRequestNumber ?? null;
  const url = run?.pullRequestUrl ?? null;

  if (actionIds.includes("open_pull_request") && number !== null && url) {
    return { mode: "open", url, number };
  }
  if (actionIds.includes("create_pull_request")) {
    return { mode: "create", url: null, number: null };
  }
  if (number !== null) {
    return { mode: "created_without_link", url: null, number };
  }
  return { mode: "none", url: null, number: null };
}

/**
 * SIMO-FORGEJO-PR-STATE-SYNC-1 — deutscher Klartext fuer den mit Forgejo
 * abgeglichenen PR-Zustand. `null`/unbekannt zeigt bewusst "Status unbekannt"
 * statt gar nichts: kein Zustand ist selbst eine Aussage (z.B. vor dem
 * ersten Abgleich), nicht einfach eine leere Flaeche.
 */
export function pullRequestStateLabel(state: string | null | undefined): string {
  switch (state) {
    case "open":
      return "Offen";
    case "closed":
      return "Geschlossen";
    case "merged":
      return "Gemergt";
    case "missing":
      return "Nicht mehr auffindbar";
    default:
      return "Status unbekannt";
  }
}

/**
 * Reine Rendering-Funktion fuer den Koerper der "Pull Request"-Sektion —
 * getrennt von `OwnerRunConsole` exportiert, damit die drei Modi
 * (`create`/`open`/`created_without_link`) ohne Mock von `api`/State direkt
 * testbar sind. `mode === "none"` liefert bewusst `null` (keine Sektion).
 *
 * SICHERHEITSKRITISCH bei `mode === "open"`: der Link bekommt `target="_blank"`
 * UND `rel="noopener noreferrer"` — ohne `noopener` koennte die geoeffnete
 * fremde Forgejo-Seite ueber `window.opener` auf dieses Fenster zugreifen,
 * ohne `noreferrer` wuerde die volle Origin als Referrer an das fremde Ziel
 * gehen. Bei `created_without_link` gibt es aus Prinzip KEIN `<a href>` — nur
 * ein ruhiger Hinweistext, niemals ein Link ohne geprueft vertrauenswuerdige
 * URL.
 */
export function renderPullRequestBody(
  presentation: PullRequestPresentation,
  options: { busy: boolean; creating: boolean; onCreate: () => void },
): JSX.Element | null {
  if (presentation.mode === "create") {
    return (
      <button
        type="button"
        onClick={options.onCreate}
        disabled={options.busy}
        className="min-h-11 inline-flex items-center justify-center rounded-md border border-jarvis-border/60 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
      >
        {options.creating ? "Wird erstellt…" : "Pull Request erstellen"}
      </button>
    );
  }
  if (presentation.mode === "open" && presentation.url) {
    return (
      <a
        href={presentation.url}
        target="_blank"
        rel="noopener noreferrer"
        className="min-h-11 inline-flex items-center gap-1 rounded-md border border-jarvis-border/60 px-3 py-1.5 text-sm font-medium"
      >
        {`Pull Request #${presentation.number} öffnen`}
        <span className="text-[11px] text-jarvis-muted">(extern, Forgejo)</span>
      </a>
    );
  }
  if (presentation.mode === "created_without_link") {
    return <p className="text-[12px] text-jarvis-muted">Der Pull Request wurde erstellt.</p>;
  }
  return null;
}

function ownerRunConsoleErrorCopy(context: "start" | "list" | "action" | "stop" | "failover"): string {
  // Wie bei `HumanStatusCard`: nie die rohe `error.message` rendern (kann
  // Provider-/Connector-Details oder interne Bezeichner enthalten) — nur ein
  // verstaendlicher, wertfreier Owner-Satz je Kontext.
  switch (context) {
    case "start":
      return "Aufgabe konnte nicht gestartet werden. Bitte erneut versuchen.";
    case "list":
      return "Aufgabenliste konnte nicht geladen werden. Bitte erneut versuchen.";
    case "stop":
      return "Lauf konnte nicht gestoppt werden. Bitte erneut versuchen.";
    case "failover":
      return "Neuer Versuch mit dem anderen Dienst konnte nicht gestartet werden.";
    case "action":
    default:
      return "Aktion konnte nicht ausgefuehrt werden. Bitte erneut versuchen.";
  }
}

/** OPNAV1 Slice 7 — Fehlerklassen trennen: Auth (401/403) ist KEIN
 *  Netzwerkflattern und Serverfehler (5xx) ist KEIN Offline. Die Klasse
 *  steuert die Owner-Ansage; transient bleibt retry-faehig. */
export type OwnerRunConsoleErrorClass = "auth" | "server" | "network" | "unknown";

export function ownerRunConsoleErrorClass(cause: unknown): OwnerRunConsoleErrorClass {
  if (cause && typeof cause === "object" && "status" in cause) {
    const status = Number((cause as { status: unknown }).status);
    if (status === 401 || status === 403) return "auth";
    if (status >= 500) return "server";
    if (status >= 400) return "unknown";
  }
  if (cause instanceof TypeError) return "network";
  return "unknown";
}

export function ownerRunConsoleErrorCopyForClass(context: "start" | "list" | "action" | "stop" | "failover", klasse: OwnerRunConsoleErrorClass): string {
  const basis = ownerRunConsoleErrorCopy(context);
  if (klasse === "auth") return `${basis} (Sitzung ungültig oder unzureichende Berechtigung — bitte neu anmelden.)`;
  if (klasse === "server") return `${basis} (Serverfehler — der Dienst antwortet gerade nicht sauber.)`;
  if (klasse === "network") return `${basis} (Verbindung zum Dienst nicht erreichbar.)`;
  return basis;
}

function humanStatusActionIds(humanStatus: Record<string, unknown> | null): string[] {
  const raw = humanStatus?.availableActions;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => (typeof entry === "object" && entry !== null ? (entry as Record<string, unknown>).id : null))
    .filter((id): id is string => typeof id === "string");
}

export function OwnerRunConsole(): JSX.Element {
  const [goal, setGoal] = useState("");
  const [actor, setActor] = useState<Actor>("codex");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [runs, setRuns] = useState<OwnerRunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  // OPNAV1 Slice 7 — Stale-Markierung: Zeitpunkt des letzten ERFOLGREICHEN
  // Ladens. Bei Folgefehlern bleibt die Liste stehen und wird als veraltet
  // gekennzeichnet — nie wird eine leere Antwort still als 0 gelesen.
  const [runsStaleSince, setRunsStaleSince] = useState<string | null>(null);

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [level, setLevel] = useState<HumanStatusLevel>("simple");
  const [humanStatus, setHumanStatus] = useState<Record<string, unknown> | null>(null);
  const [humanStatusError, setHumanStatusError] = useState<Error | null>(null);

  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // SIMO-FORGEJO-PR-STATE-SYNC-1 — Ergebnis des Forgejo-Abgleichs fuer den
  // aktuell ausgewaehlten Lauf. `null` heisst "noch kein Abgleich fuer diese
  // Auswahl versucht" (z.B. kein gespeicherter PR).
  const [prSync, setPrSync] = useState<{ synced: boolean; state: string | null } | null>(null);

  // SIMO-OWNER-CONSOLE-LIVE-UPDATES-1 — solange der SSE-Stream verbunden ist,
  // liefert er bereits Live-Updates; das Polling-Intervall unten wird dann
  // uebersprungen statt entfernt (siehe Kommentar am Polling-Effekt).
  //
  // SIMO-OWNER-CONSOLE-LIVE-UPDATES-CERTIFICATION-1 — der Zustand ist jetzt
  // dreiwertig und owner-sichtbar. `live` gilt bewusst erst, nachdem das
  // erste Frame wirklich angekommen ist (der Server sendet direkt nach dem
  // Verbindungsaufbau einen Snapshot). Vorher wurde `true` bereits vor dem
  // Lesen gesetzt — dadurch konnte das Polling uebersprungen werden, obwohl
  // noch gar keine Verbindung stand.
  const [streamStatus, setStreamStatus] = useState<OwnerLaneStreamStatus>("connecting");
  const lastSeqRef = useRef(0);

  async function loadRuns(): Promise<void> {
    setRunsLoading(true);
    try {
      const result = await api.providerLaneRuns();
      setRuns(result.runs.filter(isOwnerRunSummary));
      setRunsError(null);
      setRunsStaleSince(null);
    } catch (cause) {
      // Slice 7: Fehlerklasse differenzieren (Auth/Server/Netzwerk) und die
      // letzte erfolgreiche Liste NICHT wegwerfen — sie wird als veraltet
      // markiert, statt eine leere Antwort als "0 Aufgaben" zu zeigen.
      const klasse = ownerRunConsoleErrorClass(cause);
      setRunsError(ownerRunConsoleErrorCopyForClass("list", klasse));
      setRunsStaleSince((bisher) => bisher ?? new Date().toISOString());
    } finally {
      setRunsLoading(false);
    }
  }

  async function loadHumanStatus(runId: string, atLevel: HumanStatusLevel): Promise<void> {
    try {
      const result = await api.providerLaneHumanStatus(runId, atLevel);
      setHumanStatus(result.humanStatus);
      setHumanStatusError(null);
    } catch (cause) {
      setHumanStatus(null);
      setHumanStatusError(cause instanceof Error ? cause : new Error("human_status_unavailable"));
    }
  }

  // Läuft beim Mount und wird im Cleanup abgeräumt — höchstens alle 5
  // Sekunden ein Refresh der Arbeitsliste. Polling wird in diesem Block
  // bewusst NICHT entfernt, sondern erst nach bewiesenem Live-Betrieb
  // degradiert: solange der SSE-Stream (siehe Effekt unten) nicht verbunden
  // ist, bleibt dieses Intervall die einzige Datenquelle; ist er verbunden,
  // wird der Tick uebersprungen (kontrollierter Fallback, kein Ersatz).
  useEffect(() => {
    void loadRuns();
    const interval = setInterval(() => {
      if (streamStatus === "live") return;
      void loadRuns();
    }, RUN_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [streamStatus]);

  // SIMO-OWNER-CONSOLE-LIVE-UPDATES-1 — SSE-Stream PARALLEL zum Polling
  // oben. Bei Streamfehler oder Verbindungsabbruch: `lastSeqRef` bleibt
  // erhalten und nach 2 Sekunden wird mit `Last-Event-ID` erneut verbunden,
  // damit keine zwischenzeitliche Aenderung verloren geht.
  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    // Zaehlt aufeinanderfolgende Fehlversuche. Wird auf 0 zurueckgesetzt,
    // sobald wieder ein Frame ankommt — eine kurz gestoerte Verbindung faellt
    // dadurch nicht dauerhaft in die lange Wartezeit.
    let failedAttempts = 0;

    async function connectStream(): Promise<void> {
      if (stopped) return;
      try {
        for await (const rawEvent of api.providerLaneStream(controller.signal, lastSeqRef.current || undefined)) {
          // Erst hier steht die Verbindung nachweislich: ein Frame ist da.
          failedAttempts = 0;
          setStreamStatus("live");
          const event = sanitizeLaneStreamEvent(rawEvent);
          if (!event) continue;
          setRuns((prevRuns) => {
            const result = applyLaneStreamEvent({ runs: prevRuns, lastSeq: lastSeqRef.current }, event);
            lastSeqRef.current = result.lastSeq;
            return result.runs;
          });
        }
      } catch (streamCause) {
        // Verbindungsfehler oder Abbruch (z.B. Unmount). Der Zustand wird im
        // finally-Zweig owner-sichtbar auf "reconnecting" gesetzt; das
        // Polling deckt die Luecke ab, solange der Stream nicht steht.
        // OPNAV1 Slice 7: Auth-Fehler (401/403) sind KEIN Flattern — hier
        // wird kein endloser Stream-Retry aufgebaut; das Polling bleibt als
        // kontrollierter Fallback und meldet die Klasse eigenständig.
        if (ownerRunConsoleErrorClass(streamCause) === "auth") {
          stopped = true;
          controller.abort();
        }
      } finally {
        if (!stopped) {
          setStreamStatus("reconnecting");
          failedAttempts += 1;
          const delay = laneStreamRetryDelayMs(failedAttempts, Math.random());
          retryTimer = setTimeout(() => void connectStream(), delay);
        }
      }
    }

    void connectStream();

    return () => {
      stopped = true;
      controller.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  useEffect(() => {
    if (!selectedRunId) {
      setHumanStatus(null);
      setHumanStatusError(null);
      return;
    }
    void loadHumanStatus(selectedRunId, level);
  }, [selectedRunId, level]);

  /**
   * SIMO-FORGEJO-PR-STATE-SYNC-1 — genau EIN Abgleich mit Forgejo, gebunden
   * an die Auswahl des Laufs (nicht an jedes Re-Rendern und nicht an jede
   * Aenderung der Laufliste durch Polling/SSE — deshalb bewusst nur
   * `selectedRunId` als Abhaengigkeit). Ohne gespeicherten PR passiert nichts.
   */
  useEffect(() => {
    setPrSync(null);
    if (!selectedRunId) return;
    const run = runs.find((entry) => entry.runId === selectedRunId);
    if (!run || run.pullRequestNumber === null || run.pullRequestNumber === undefined) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await api.providerLanePullRequestSync(selectedRunId);
        if (!cancelled) setPrSync({ synced: result.synced, state: result.state });
      } catch {
        if (!cancelled) setPrSync({ synced: false, state: null });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRunId]);

  async function startTask(): Promise<void> {
    const trimmedGoal = goal.trim();
    if (!trimmedGoal || starting) return;
    setStarting(true);
    setStartError(null);
    try {
      const result = await api.providerLaneStartRun({ actor, goal: trimmedGoal });
      if (result.ok && result.run) {
        setGoal("");
        setSelectedRunId(result.run.runId);
        await loadRuns();
      } else {
        setStartError(ownerRunConsoleErrorCopy("start"));
      }
    } catch {
      setStartError(ownerRunConsoleErrorCopy("start"));
    } finally {
      setStarting(false);
    }
  }

  async function applyOperation(operation: string): Promise<void> {
    if (!selectedRunId || actionBusy) return;
    setActionBusy(operation);
    setActionError(null);
    try {
      await api.providerLaneApprove(selectedRunId, operation);
      await api.providerLaneExecute(selectedRunId, operation);
      await Promise.all([loadRuns(), loadHumanStatus(selectedRunId, level)]);
    } catch {
      setActionError(ownerRunConsoleErrorCopy("action"));
    } finally {
      setActionBusy(null);
    }
  }

  /**
   * SIMO-FORGEJO-PR-LANE-1 (Teil 2). Genau derselbe Freigeben+Ausfuehren-Pfad
   * wie `applyOperation`, nur fuer die feste Operation `create_pr` — der
   * Server entscheidet ueber Erreichbarkeit/Erfolg, die Konsole loest nur aus.
   */
  async function createPullRequest(): Promise<void> {
    if (!selectedRunId || actionBusy) return;
    setActionBusy("create_pr");
    setActionError(null);
    try {
      await api.providerLaneApprove(selectedRunId, "create_pr");
      await api.providerLaneExecute(selectedRunId, "create_pr");
      await Promise.all([loadRuns(), loadHumanStatus(selectedRunId, level)]);
    } catch {
      setActionError(ownerRunConsoleErrorCopy("action"));
    } finally {
      setActionBusy(null);
    }
  }

  async function stopSelectedRun(): Promise<void> {
    if (!selectedRunId || actionBusy) return;
    setActionBusy("stop");
    setActionError(null);
    try {
      await api.providerLaneStop(selectedRunId, "owner_stop");
      await Promise.all([loadRuns(), loadHumanStatus(selectedRunId, level)]);
    } catch {
      setActionError(ownerRunConsoleErrorCopy("stop"));
    } finally {
      setActionBusy(null);
    }
  }

  async function failoverSelectedRun(): Promise<void> {
    if (!selectedRunId || actionBusy) return;
    const currentRun = runs.find((run) => run.runId === selectedRunId);
    const nextActor = otherActor(currentRun?.actor ?? actor);
    setActionBusy("failover");
    setActionError(null);
    try {
      const result = await api.providerLaneFailover(selectedRunId, nextActor);
      if (result.ok && result.nextRunId) {
        setSelectedRunId(result.nextRunId);
      }
      await loadRuns();
    } catch {
      setActionError(ownerRunConsoleErrorCopy("failover"));
    } finally {
      setActionBusy(null);
    }
  }

  const grouped = groupRunsForOwner(runs);
  const selectedRun = runs.find((run) => run.runId === selectedRunId) ?? null;
  const detailActionIds = humanStatusActionIds(humanStatus);
  const approveOps = approvableOperations(detailActionIds);
  const canFailover = detailActionIds.includes("failover_other_provider");
  const canStop = selectedRun?.state === "running";
  const prPresentation = pullRequestPresentation(detailActionIds, selectedRun);

  function renderRunGroup(title: string, items: OwnerRunSummary[]): JSX.Element | null {
    if (items.length === 0) return null;
    return (
      <div key={title} className="space-y-1">
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-jarvis-muted">
          {title} ({items.length})
        </h3>
        <ul className="space-y-1">
          {items.map((run) => (
            <li key={run.runId}>
              <button
                type="button"
                onClick={() => setSelectedRunId(run.runId)}
                aria-pressed={run.runId === selectedRunId}
                className={`w-full rounded-md border px-2 py-1.5 text-left text-[12px] ${
                  run.runId === selectedRunId
                    ? "border-jarvis-accent/60 bg-jarvis-accent/10"
                    : "border-jarvis-border/45 bg-jarvis-bg/25"
                }`}
              >
                <span className="font-medium text-jarvis-text">{serviceLabel(run.actor)}</span>
                <span className="ml-2 text-jarvis-muted">{ownerStateLabel(run.state)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // `break-words` am Wurzelelement: lange Ueberschriften, Fehlermeldungen und
  // PR-Links duerfen auf schmalen Displays nicht horizontal scrollen lassen.
  return (
    <div className="space-y-4 break-words" data-owner-run-console>
      {/* 1. Aufgabenfeld */}
      <section aria-label="Aufgabe starten" className="card space-y-2 p-4">
        <h2 className="text-sm font-semibold">Aufgabe starten</h2>
        <div>
          <label htmlFor="owner-run-console-goal" className="mb-1 block text-xs text-jarvis-muted">
            Aufgabe
          </label>
          <textarea
            id="owner-run-console-goal"
            value={goal}
            onChange={(event) => setGoal(event.target.value)}
            placeholder="Was soll SIMO tun? In eigenen Worten beschreiben."
            rows={3}
            className="min-h-11 inline-flex items-center justify-center w-full rounded-md border border-jarvis-border/60 bg-jarvis-bg/40 px-2 py-1.5 text-sm outline-none focus:border-jarvis-accent/60"
          />
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label htmlFor="owner-run-console-actor" className="mb-1 block text-xs text-jarvis-muted">
              Dienst
            </label>
            <select
              id="owner-run-console-actor"
              value={actor}
              onChange={(event) => setActor(event.target.value as Actor)}
              className="min-h-11 inline-flex items-center justify-center rounded-md border border-jarvis-border/60 bg-jarvis-bg/40 px-2 py-1.5 text-sm"
            >
              {ACTORS.map((entry) => (
                <option key={entry} value={entry}>
                  {serviceLabel(entry)}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => void startTask()}
            disabled={starting || !goal.trim()}
            className="min-h-11 inline-flex items-center justify-center rounded-md bg-jarvis-accent/80 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            {starting ? "Wird gestartet…" : "Aufgabe starten"}
          </button>
        </div>
        {startError && <p className="text-[12px] text-rose-300">{startError}</p>}
      </section>

      {/* 2. Arbeitsliste */}
      <section aria-label="Arbeitsliste" className="card space-y-2 p-4">
        <h2 className="text-sm font-semibold">Aufgaben</h2>
        {/*
          SIMO-OWNER-CONSOLE-LIVE-UPDATES-CERTIFICATION-1 — der Owner sieht,
          ob die Liste gerade live mitlaeuft. `role="status"` meldet den
          Wechsel auch an Screenreader, ohne den Fokus zu stehlen.
        */}
        <p
          role="status"
          data-testid="owner-run-console-stream-status"
          data-stream-status={streamStatus}
          className={`break-words text-[12px] ${
            streamStatus === "reconnecting" ? "text-amber-300" : "text-jarvis-muted"
          }`}
        >
          {laneStreamStatusLabel(streamStatus)}
        </p>
        {runsLoading && runs.length === 0 ? (
          <p className="text-[12px] text-jarvis-muted">Wird geladen …</p>
        ) : runs.length === 0 ? (
          <p className="text-[12px] text-jarvis-muted">Noch keine Aufgaben.</p>
        ) : (
          <div className="space-y-3">
            {renderRunGroup("Läuft", grouped.running)}
            {renderRunGroup("Wartet auf dich", grouped.waiting)}
            {renderRunGroup("Fehlgeschlagen", grouped.failed)}
            {renderRunGroup("Abgeschlossen", grouped.done)}
          </div>
        )}
        {runsError && <p className="text-[12px] text-rose-300" role="alert">{runsError}</p>}
        {runsError && runsStaleSince && (
          <p className="text-[11px] text-amber-300" role="status" data-testid="runs-stale-marker">
            Angezeigte Liste ist veraltet — Stand {new Date(runsStaleSince).toLocaleTimeString("de-DE")}. Es wird weiter versucht, aktuelle Daten zu laden; eine leere Antwort wird nicht als „keine Aufgaben" gedeutet.
          </p>
        )}
      </section>

      {/* 3. Detail */}
      <section aria-label="Aufgabendetail" className="card space-y-2 p-4">
        <h2 className="text-sm font-semibold">Details</h2>
        {selectedRunId ? (
          <HumanStatusCard status={humanStatus} level={level} onLevelChange={setLevel} error={humanStatusError} />
        ) : (
          <p className="text-[12px] text-jarvis-muted">Wähle eine Aufgabe aus der Liste, um Details zu sehen.</p>
        )}
      </section>

      {/* 4. Freigaben — nur wenn `availableActions` tatsächlich `approve_operations` enthält. */}
      {selectedRunId && approveOps.length > 0 && (
        <section aria-label="Freigaben" className="card space-y-2 p-4">
          <h2 className="text-sm font-semibold">Freigaben</h2>
          <ul className="flex flex-wrap gap-2">
            {approveOps.map((operation) => (
              <li key={operation}>
                <button
                  type="button"
                  onClick={() => void applyOperation(operation)}
                  disabled={actionBusy !== null}
                  className="min-h-11 inline-flex items-center justify-center rounded-md border border-jarvis-border/60 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  {actionBusy === operation ? "Wird ausgeführt…" : OPERATION_LABELS[operation]}
                </button>
              </li>
            ))}
          </ul>
          {actionError && <p className="text-[12px] text-rose-300">{actionError}</p>}
        </section>
      )}

      {/* 4b. Pull Request — SIMO-FORGEJO-PR-LANE-1 (Teil 2). Erstellen-Knopf
          verschwindet automatisch, sobald der Server `open_pull_request`
          oder `created_without_link` meldet (kein `create`-Modus mehr). */}
      {selectedRunId && prPresentation.mode !== "none" && (
        <section aria-label="Pull Request" className="card space-y-2 p-4">
          <h2 className="text-sm font-semibold">Pull Request</h2>
          {renderPullRequestBody(prPresentation, {
            busy: actionBusy !== null,
            creating: actionBusy === "create_pr",
            onCreate: () => void createPullRequest(),
          })}
          {/* SIMO-FORGEJO-PR-STATE-SYNC-1 — Zustands-Text neben dem PR-Link,
              nur wenn tatsaechlich eine PR-Nummer bekannt ist. Der Link selbst
              bleibt in jedem Fall sichtbar, auch wenn der Abgleich fehlschlug. */}
          {prPresentation.number !== null && (
            <p className="text-[12px] text-jarvis-muted break-words">
              Status: {pullRequestStateLabel(prSync?.state ?? selectedRun?.pullRequestState ?? null)}
            </p>
          )}
          {prSync && prSync.synced === false && (
            <p className="text-[12px] text-amber-300 break-words">
              Status konnte nicht mit Forgejo abgeglichen werden — angezeigt ist der zuletzt bekannte Stand.
            </p>
          )}
          {actionError && prPresentation.mode === "create" && <p className="text-[12px] text-rose-300">{actionError}</p>}
        </section>
      )}

      {/* 5. Folgeaktionen — nur bei tatsächlich verfügbaren `availableActions`. */}
      {selectedRunId && (canFailover || canStop) && (
        <section aria-label="Folgeaktionen" className="card space-y-2 p-4">
          <h2 className="text-sm font-semibold">Folgeaktionen</h2>
          <div className="flex flex-wrap gap-2">
            {canFailover && (
              <button
                type="button"
                onClick={() => void failoverSelectedRun()}
                disabled={actionBusy !== null}
                className="min-h-11 inline-flex items-center justify-center rounded-md border border-jarvis-border/60 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              >
                {actionBusy === "failover" ? "Wird gestartet…" : "Mit dem anderen KI-Dienst erneut versuchen"}
              </button>
            )}
            {canStop && (
              <button
                type="button"
                onClick={() => void stopSelectedRun()}
                disabled={actionBusy !== null}
                className="min-h-11 inline-flex items-center justify-center rounded-md border border-rose-400/50 px-3 py-1.5 text-sm font-medium text-rose-200 disabled:opacity-50"
              >
                {actionBusy === "stop" ? "Wird gestoppt…" : "Lauf stoppen"}
              </button>
            )}
          </div>
          {/* `approveOps` (Freigaben) und Folgeaktionen sind serverseitig disjunkt
              (siehe `buildRunNextActions`): ein Lauf liefert nie gleichzeitig
              `approve_operations` und `failover_other_provider`/laufenden Stop —
              die Fehlermeldung kann deshalb hier ohne Duplikatsrisiko stehen. */}
          {actionError && approveOps.length === 0 && <p className="text-[12px] text-rose-300">{actionError}</p>}
        </section>
      )}
    </div>
  );
}
